import classnames from 'classnames';
import React from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'success' | 'danger' | 'warning' | 'info';
  size?: 'sm' | 'md' | 'lg' | 'full';
  children?: React.ReactNode;
  title?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void | Promise<void>;
  disabled?: boolean;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ variant = 'primary', size = 'full', fullWidth = false, className, children, ...props }) => {
  // Base Tailwind classes using aware variables
  const baseClasses = 'cursor-pointer inline-flex items-center justify-center rounded-md font-medium transition-normal focus:outline-none focus:ring-2 focus:ring-offset-2';

  // Theme-aware variant classes using CSS custom properties
  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-primary-hover focus:ring-primary',
    secondary: 'bg-secondary text-white hover:bg-secondary-hover focus:ring-secondary',
    outline: 'border border-border bg-surface text-text hover:bg-surface-hover focus:ring-primary',
    success: 'bg-success text-white hover:bg-success-hover focus:ring-success',
    danger: 'bg-danger text-white hover:bg-danger-hover focus:ring-danger',
    warning: 'bg-warning text-white hover:bg-warning-hover focus:ring-warning',
    info: 'bg-info text-white hover:bg-info-hover focus:ring-info',
  };

  // Theme-aware size classes
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-5 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
    full: 'w-full px-5 py-3 text-base',
  };

  // Combine all classes using theme utility
  const combinedClasses = useThemeClasses(classnames(baseClasses, variantClasses[variant], sizeClasses[size], className));

  return (
    <button className={combinedClasses} {...props}>
      {children}
    </button>
  );
};

export default Button;
